import { Alert, Button, Descriptions, Form, Space, Typography } from "antd";
import { useState } from "react";
import dayjs from "dayjs";
import View from "../controls/View";
import InModal from "../controls/InModal";
import { PlatformUser } from "../repository/data.default";
import { deleteUser, getUser, updateUserProfile } from "../repository/data.repository";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import useUserProfileCard from "../hooks/useUserProfileCard";
import UserProfileActions from "./user-card/UserProfileActions";
import UserActionDialogs from "./user-card/UserActionDialogs";
import UserMetricsPanel from "./user-card/UserMetricsPanel";
import CompanyProfileSection from "./user-card/CompanyProfileSection";
import {
  userIdentityFields,
  userContactFields,
  userLockoutFields,
} from "../controls/userProfileFields";
import { FieldConfig, renderFields } from "../controls/DescriptionsFields";

export interface UserViewProps {
  readonly uid?: string | null;
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete" | "view";
  readonly onFinish?: () => void;
  readonly onClose?: () => void;
}

const identityFields: FieldConfig[] = userIdentityFields();

function UserView(props: UserViewProps) {
  const permissions = useSelector(authorizeSelectors.permissions);
  const card = useUserProfileCard({
    uid: props.uid,
    open: props.open,
    getFn: getUser,
    updateFn: updateUserProfile,
    withHistory: true,
  });
  const { data, editor } = card;
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(false);

  const canEdit = permissions.can("PlatformUsers", "create");

  function onDelete(): void {
    setDeleting(true);
    setError(false);
    deleteUser(props.uid!)
      .then(() => {
        setDeleting(false);
        props.onFinish?.();
      })
      .catch(() => {
        setDeleting(false);
        setError(true);
      });
  }

  const contactFields: FieldConfig[] = userContactFields(data, {
    editable: true,
    email: { onConfirm: canEdit ? () => card.onInputOpen("confirmEmail") : undefined },
    phone: { onConfirm: canEdit ? () => card.onInputOpen("confirmPhone") : undefined },
  });

  return (
    <>
      <View
        open={["view"].includes(props.mode) && props.open}
        loading={card.loading}
        title={"User"}
        onBack={() => props.onClose?.()}
        actions={
          <UserProfileActions
            canEdit={canEdit}
            editor={editor}
            item={card.item}
            onInputOpen={card.onInputOpen}
            show={{ assignReward: true, lock: true }}
          />
        }
      >
        <Form form={editor.form} component={false}>
          <div className="flex flex-col gap-4 p-4">
            <div className="grid grid-cols-2 gap-4">
              <Descriptions
                title="Identity"
                bordered
                size="small"
                column={2}
                items={renderFields(identityFields, data, editor.editing)}
              />
              <Descriptions
                title="Contact"
                bordered
                size="small"
                column={1}
                items={renderFields(contactFields, data, editor.editing)}
              />
            </div>

            <CompanyProfileSection
              data={data}
              editing={editor.editing}
              companyPhoneOnConfirm={
                canEdit ? () => card.onInputOpen("confirmCompanyPhone") : undefined
              }
            />

            <div className="grid grid-cols-2 gap-4">
              <Descriptions
                title="Security"
                bordered
                size="small"
                column={2}
                items={renderFields(userLockoutFields({ editable: true }), data, editor.editing)}
              />
              <Descriptions
                title="Account"
                bordered
                size="small"
                column={2}
                items={[
                  { key: "createdOn", label: "Created On", children: data?.createdOn ? dayjs(data.createdOn).format("DD/MM/YYYY HH:mm") : undefined },
                  { key: "lastLoginAt", label: "Last Login", children: data?.lastLoginAt ? dayjs(data.lastLoginAt).format("DD/MM/YYYY HH:mm") : undefined },
                  { key: "loyaltyPoints", label: "Loyalty Points", children: data?.loyaltyPoints },
                  // PD-01 4.3 asks for a "reset status" beside the activation fields. The honest answer is
                  // WHEN a reset was last sent: whether the user then used the link is not knowable here,
                  // because consuming the token changes the password rather than setting a flag. Empty
                  // means none has ever been sent.
                  { key: "passwordResetRequestedAt", label: "Password Reset Sent", children: data?.passwordResetRequestedAt ? dayjs(data.passwordResetRequestedAt).format("DD/MM/YYYY HH:mm") : undefined },
                ]}
              />
            </div>

            <UserMetricsPanel history={card.history} loyaltyPoints={data?.loyaltyPoints} lastLoginAt={data?.lastLoginAt} createdOn={data?.createdOn} />
          </div>
        </Form>
      </View>

      <InModal
        open={["delete"].includes(props.mode) && props.open}
        size="small"
        title={"Delete User"}
        onCancel={() => props.onClose?.()}
        actions={
          <Space className="flex flex-row">
            <Button type={"primary"} danger loading={deleting} onClick={onDelete}>
              Delete
            </Button>
          </Space>
        }
      >
        <Typography.Paragraph>
          <Typography.Text>
            Continue deleting the{" "}
            <Typography.Text className="font-bold">{(card.item as PlatformUser | null)?.userName}</Typography.Text>{" "}
            user?
          </Typography.Text>
        </Typography.Paragraph>
        {error && (
          <Alert className="mb-2" message="Something went wrong, try again" type="error" />
        )}
      </InModal>

      <UserActionDialogs
        item={card.item}
        inputMode={card.inputMode}
        inputOpen={card.inputOpen}
        onClose={card.onInputClose}
        onFinish={card.onInputFinish}
      />
    </>
  );
}

export default UserView;
